import {
  holdingDisplayName,
  isSummaryHolding,
  positionHoldings,
  toNumber,
} from './calc';

/** Maps normalized CSV header tokens to holding field names. */
const FIELD_ALIASES = {
  name: ['name', 'company', 'equities', 'equity', 'security'],
  ticker: ['ticker', 'symbol', 'tick'],
  shares: ['shares', 'share', 'qty', 'quantity'],
  costBasis: ['cost_basis', 'costbasis', 'cost'],
  currentPrice: ['current_price', 'currentprice', 'price', 'last_price', 'lastprice'],
  marketValue: ['market_value', 'marketvalue', 'value', 'mv'],
  dividendYield: ['dividend_yield', 'dividendyield', 'div_yield', 'divyield', 'yield'],
  cashDividends: ['cash_dividends', 'cashdividends', 'cash_div', 'cashdiv'],
  reinvestedDividends: [
    'reinvested_dividends',
    'reinvesteddividends',
    'reinv_div',
    'reinvdiv',
  ],
};

const normalizeHeaderToken = (header) =>
  String(header || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

/** Build a map of holding field -> original CSV column name. */
export const mapCsvHeaders = (headers = []) => {
  const mapping = {};
  headers.forEach((header) => {
    const token = normalizeHeaderToken(header);
    if (!token) return;
    Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => {
      if (mapping[field]) return;
      if (aliases.includes(token)) mapping[field] = header;
    });
  });
  return mapping;
};

const parseFieldValue = (field, raw) => {
  if (field === 'name') return String(raw).trim();
  if (field === 'ticker') return String(raw).trim().toUpperCase();
  const n = toNumber(raw);
  return n !== null ? n : '';
};

/** Parse one Papa-parsed CSV row into a holding patch with `_present` flags. */
export const parseCsvRow = (row, headerMap) => {
  const parsed = { _present: {} };

  Object.entries(headerMap).forEach(([field, csvKey]) => {
    if (!csvKey || row[csvKey] === undefined || row[csvKey] === null) return;
    const raw = String(row[csvKey]).trim();
    if (raw === '' || raw === '--') return;
    parsed._present[field] = true;
    parsed[field] = parseFieldValue(field, raw);
  });

  return parsed;
};

/** Merge CSV fields into an existing holding; empty CSV cells leave fields unchanged. */
export const mergeHoldingFields = (existing, incoming) => {
  const out = { ...existing };
  const textFields = ['name', 'ticker'];
  const numericFields = [
    'shares',
    'costBasis',
    'currentPrice',
    'marketValue',
    'dividendYield',
    'cashDividends',
    'reinvestedDividends',
  ];

  textFields.forEach((field) => {
    if (incoming._present?.[field]) out[field] = incoming[field] ?? '';
  });
  numericFields.forEach((field) => {
    if (incoming._present?.[field]) out[field] = incoming[field] ?? '';
  });

  return out;
};

const matchKey = (holding) => {
  const ticker = (holding.ticker || '').trim().toUpperCase();
  if (ticker) return `ticker:${ticker}`;
  const name = holdingDisplayName(holding).toLowerCase();
  if (name) return `name:${name}`;
  return null;
};

/**
 * Build a merge plan from parsed CSV rows and existing Firestore holdings.
 * Returns { ok, errors, updates, adds, skipped }.
 */
export const buildImportPlan = (parsedRows, existingHoldings = []) => {
  const positions = positionHoldings(existingHoldings);
  const byTicker = new Map();
  const byName = new Map();

  positions.forEach((h) => {
    const ticker = (h.ticker || '').trim().toUpperCase();
    if (ticker) byTicker.set(ticker, h);
    const name = holdingDisplayName(h).toLowerCase();
    if (name) byName.set(name, h);
  });

  const errors = [];
  const skipped = [];
  const updates = [];
  const adds = [];
  const seenCsvTickers = new Map();

  parsedRows.forEach((incoming, index) => {
    const rowNum = index + 2;
    const label = incoming.ticker || holdingDisplayName(incoming) || `row ${rowNum}`;

    if (isSummaryHolding(incoming)) {
      skipped.push({ label, reason: 'Summary row (use Cash balance field for cash)' });
      return;
    }

    if (!incoming.ticker && !incoming.name) {
      skipped.push({ label: `row ${rowNum}`, reason: 'Missing name and ticker' });
      return;
    }

    if (incoming.ticker) {
      const t = incoming.ticker.trim().toUpperCase();
      if (seenCsvTickers.has(t)) {
        errors.push(`Duplicate ticker in CSV: ${t}`);
      } else {
        seenCsvTickers.set(t, rowNum);
      }
    }

    let existing = null;
    if (incoming.ticker) existing = byTicker.get(incoming.ticker.trim().toUpperCase());
    if (!existing && incoming.name) {
      existing = byName.get(holdingDisplayName(incoming).toLowerCase());
    }

    if (existing) {
      updates.push({
        key: matchKey(existing),
        label: existing.ticker || holdingDisplayName(existing),
        existing,
        incoming,
        merged: mergeHoldingFields(existing, incoming),
      });
    } else {
      const merged = mergeHoldingFields(
        {
          name: '',
          ticker: '',
          shares: '',
          costBasis: '',
          currentPrice: '',
          marketValue: '',
          dividendYield: '',
          cashDividends: '',
          reinvestedDividends: '',
        },
        incoming
      );
      adds.push({ key: matchKey(merged) || `new:${rowNum}`, label, merged });
    }
  });

  if (errors.length > 0) {
    return { ok: false, errors, updates: [], adds: [], skipped };
  }

  return { ok: true, errors: [], updates, adds, skipped };
};

/** Apply a merge plan and return the full holdings array ready to persist. */
export const applyImportPlan = (plan, existingHoldings = []) => {
  const positions = positionHoldings(existingHoldings);
  const updatedById = new Map(plan.updates.map((u) => [u.existing.id, u.merged]));

  const merged = positions.map((h) => (h.id && updatedById.has(h.id) ? updatedById.get(h.id) : h));
  plan.adds.forEach((a) => merged.push(a.merged));

  return merged;
};
